import styles from "./CartDrawer.module.css";
import {
    ATTRIBUTE_KEY_SHORTCODES,
    CartItemPositions,
    getPriceLabel,
    isSpiritEmbassyShop,
} from "../../Bundles/constants";
import { CartConfigInterface, CartData, CartLineItem } from "../CartTypes";
import { CartOperations, CartOperationsTypes } from "./cartConstants";
import DiscountIcon from "../CartIcons/DiscountIcon";
import DiscountIconBadge from "../CartIcons/DiscountIconBadge";
import DeleteIcon from "../CartIcons/DeleteIcon";
import { useEffect, useState } from "react";
import Spinner from "../../../components/Icons/Spinner";
import useCartDrawerLayoutHook from "../../Bundles/useCartDrawerLayoutHook";
import CartItemVariantsInfo from "../../../components/CartItemVariantsInfo/CartItemVariantsInfo";
import { MAYUR_TEST_STORE, getProductId } from "../../constants";

export const DefaultImageIcon = ({ w = "100%", h = "100%" }: { w?: any; h?: any; fill?: string }) => (
    <svg width={w} height={h} viewBox="0 0 128 128" fill={"#ababab"} xmlns="http://www.w3.org/2000/svg">
        <g>
            <rect width="128" height="128" rx="4" fill={"#ababab"} />
            <mask id="mask0_1259_5606" maskUnits="userSpaceOnUse" x="0" y="0" width="128" height="128">
                <rect width="128" height="128" rx="4" />
            </mask>
            <g mask="url(#mask0_1259_5606)">
                <path
                    d="M0 126.897H128C128 119.847 128 116.463 128 103.711V63.1339L108.308 39.9473L57.1077 95.5953L34.4615 68.9306L1.66893e-06 106.029C1.66893e-06 115.147 4.86374e-05 118.672 0 126.897Z"
                    fill={"#828282"}
                />
                <path
                    d="M42.7781 49.1481C51.1939 49.1481 58.0162 42.3257 58.0162 33.91C58.0162 25.4942 51.1939 18.6719 42.7781 18.6719C34.3624 18.6719 27.54 25.4942 27.54 33.91C27.54 42.3257 34.3624 49.1481 42.7781 49.1481Z"
                    fill={"#f4f4f4"}
                    stroke={"#f4f4f4"}
                    strokeWidth="2"
                    strokeMiterlimit="10"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </g>
        </g>
    </svg>
);

export const RenderDiscounts = ({ arr = [], textColor = "" }: { arr: any[]; textColor?: string }) => {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 4,
                color: textColor || "inherit",
                flexWrap: "wrap",
                flexGrow: 1,
            }}
        >
            <span style={{ position: "relative", bottom: -1 }}>
                <DiscountIcon color={textColor} />
            </span>
            {arr.map((dis, idx) => {
                const data = {
                    ...dis,
                    ...(dis?.discount_application && dis?.discount_application),
                };
                return (
                    <span
                        key={idx}
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "max-content",
                        }}
                    >
                        {data?.title}
                        {idx < arr.length - 1 ? "," : ""}
                    </span>
                );
            })}
        </div>
    );
};

export const RenderDiscountsBadges = ({
    arr = [],
    bgColor = "#8A8A8A42",
    textColor = "#5A5656",
    showIcon = true,
}: {
    arr: any[];
    bgColor?: string;
    textColor?: string;
    showIcon?: boolean;
}) => {
    const newArr = arr?.map((dis) => {
        const data = {
            ...dis,
            ...(dis?.discount_application && dis?.discount_application),
        };
        return data;
    });

    return newArr?.map((discount, idx) => {
        return (
            <div
                style={{
                    display: "flex",
                    gap: 4,
                    alignItems: "center",
                    background: bgColor,
                    color: textColor,
                    borderRadius: 8,
                    padding: "3px 5px",
                    fontSize: 9,
                    height: "min-content",
                }}
                key={idx + discount?.title}
            >
                {showIcon && (
                    <span
                        style={{
                            position: "relative",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <DiscountIconBadge size={10} color={textColor} />
                    </span>
                )}
                {discount.title}
            </div>
        );
    });
};
const CartLine = ({
    cartLineItem,
    index,
    cart,
    handleUpdateItem,
    currency,
    money_format,
    actionInProgress = false,
    cartConfig,
    standAlone,
    sft,
    clearCart,
    allCartLinesCount,
}: {
    cartLineItem: CartLineItem;
    index: number;
    handleUpdateItem: any;
    currency: string;
    money_format: string;
    actionInProgress: boolean;
    cartConfig: CartConfigInterface;
    standAlone: boolean;
    cart: CartData;
    sft: string;
    clearCart: () => void;
    allCartLinesCount: number;
}) => {
    const { cartGlobal, productCardLayout } = useCartDrawerLayoutHook({
        config: cartConfig,
    });

    const userShopname = !standAlone ? shopify?.config?.shop : window?.kefi_bundles?.shop;
    const [productOptions, setProductOptions] = useState(cartLineItem?.options_with_values);
    const [microActionProgress, setMicroActionProgress] = useState<null | number>(null);
    useEffect(() => {
        if (cartConfig?.cart_settings?.show_product_properties && cartLineItem?.options_with_values?.length) {
            setProductOptions(
                cartLineItem?.options_with_values.filter(
                    (option) =>
                        option?.name?.toLowerCase() !== "title" && option?.value?.toLowerCase() !== "default title",
                ),
            );
        } else {
            setProductOptions([]);
        }
        setMicroActionProgress(null);
    }, [cartLineItem]);

    const handleQuantity = (e: any, operation: CartOperationsTypes, id: string) => {
        const stepSize = cartLineItem?.quantity_rule?.increment ?? 1;
        let newValue = 0;
        if (operation != "clear") {
            newValue = operation == "decrease" ? cartLineItem?.quantity - stepSize : cartLineItem?.quantity + stepSize;
        }
        const lineIdx = cart?.items.findIndex((cl) => cl.key == id);
        if (lineIdx >= 0)
            handleUpdateItem({
                line: lineIdx + 1,
                quantity: +newValue,
            });
    };

    const RenderDelete = (
        <div
            role="button"
            tabIndex={0}
            aria-label={`Remove ${cartLineItem?.product_title || "item"}`}
            className={styles.removeButton}
            style={{ color: actionInProgress ? "#9a9a9a" : "" }}
            onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                e.currentTarget.click();
            }}
            onClick={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
                setMicroActionProgress(index);

                const rewardKey =
                    cartLineItem.properties?.[ATTRIBUTE_KEY_SHORTCODES.__kbd]?.[
                        ATTRIBUTE_KEY_SHORTCODES.__triggerRewards
                    ]?.rewardKey;

                if (rewardKey) {
                    const map = JSON.parse(sessionStorage.getItem("removedFreeGifts") || "{}");
                    map[rewardKey] = true;
                    sessionStorage.setItem("removedFreeGifts", JSON.stringify(map));
                }

                if (allCartLinesCount === 1) {
                    clearCart();
                    sessionStorage.setItem("ap_status", "off");
                } else {
                    handleQuantity(e, CartOperations.CLEAR, cartLineItem.key);
                }
            }}
        >
            <DeleteIcon size={17} />
        </div>
    );

    const RenderPrice = (
        <div className={styles.cartItemPrice}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems:
                        productCardLayout.flexDirection == "column" ||
                        productCardLayout.flexDirection == "column-reverse"
                            ? "flex-end"
                            : "baseline",
                    flexDirection: productCardLayout.flexDirection,
                    flexWrap: "wrap",
                    color: cartConfig?.cart_design?.cartline_card_text_color,
                    gap: 2,
                }}
            >
                {(cartConfig.cart_settings.show_compare_at_price || !!cartLineItem?.discounts.length) &&
                    cartLineItem?.original_line_price != cartLineItem?.final_line_price && (
                        <span
                            className={styles.cartItemPriceOriginal}
                            style={{
                                fontSize: cartConfig?.cart_design?.cartline_card_compared_price_size,
                                fontWeight: cartConfig?.cart_design?.cartline_card_compared_price_style?.split(" ")[0],
                                fontStyle: cartConfig?.cart_design?.cartline_card_compared_price_style?.split(" ")[1],
                            }}
                        >
                            {getPriceLabel(cartLineItem?.original_line_price / 100, money_format, currency, false)}
                        </span>
                    )}
                <span
                    className={styles.cartItemPriceDiscounted}
                    style={{
                        fontSize: cartConfig?.cart_design?.cartline_card_price_size,
                        fontWeight: cartConfig?.cart_design?.cartline_card_price_style?.split(" ")[0],
                        fontStyle: cartConfig?.cart_design?.cartline_card_price_style?.split(" ")[1],
                    }}
                >
                    &nbsp;
                    {getPriceLabel(cartLineItem?.final_line_price / 100, money_format, currency, false)}
                </span>
            </div>

            {cartConfig?.cart_settings?.show_total_saved &&
                !!cartLineItem?.discounts.length &&
                cartLineItem?.original_line_price != cartLineItem?.final_line_price && (
                    <span
                        className={styles.savePrice}
                        style={{
                            color: cartConfig?.cart_design?.cartline_card_save_text_color,
                            fontSize: cartConfig?.cart_design?.cartline_card_save_text_size,
                            fontWeight: cartConfig?.cart_design?.cartline_card_save_text_style?.split(" ")[0],
                            fontStyle: cartConfig?.cart_design?.cartline_card_save_text_style?.split(" ")[1],
                        }}
                    >
                        {productCardLayout.pricePosition == CartItemPositions.TOP ? (
                            <span>
                                {cartConfig?.saving_label_text}{" "}
                                {getPriceLabel(
                                    (cartLineItem?.original_line_price - cartLineItem?.final_line_price) / 100,
                                    money_format,
                                    currency,
                                )}
                            </span>
                        ) : (
                            <>
                                ({cartConfig?.saving_label_text}{" "}
                                {getPriceLabel(
                                    (cartLineItem?.original_line_price - cartLineItem?.final_line_price) / 100,
                                    money_format,
                                    currency,
                                    false,
                                )}
                                )
                            </>
                        )}
                    </span>
                )}
        </div>
    );

    const ItemOriginalPrice = (
        <span
            className={styles.cartPricePerItem}
            style={{
                color: cartConfig?.cart_design?.cartline_card_text_color,
                fontSize: cartConfig?.cart_design?.cartline_card_compared_price_size,
                fontWeight: cartConfig?.cart_design?.cartline_card_compared_price_style?.split(" ")[0],
                fontStyle: cartConfig?.cart_design?.cartline_card_compared_price_style?.split(" ")[1],
            }}
        >
            {getPriceLabel(cartLineItem?.original_price / 100, money_format, currency, false)}
        </span>
    );

    const RenderSellingPlan = !!cartLineItem?.selling_plan_allocation?.selling_plan?.name && (
        <span style={{ fontSize: 12, fontWeight: 400 }}>
            {cartLineItem?.selling_plan_allocation?.selling_plan?.name}
        </span>
    );

    const shopCheck =
        userShopname &&
        ["beautybay-3691.myshopify.com", MAYUR_TEST_STORE, "anurag-1010.myshopify.com"].includes(userShopname) &&
        cartLineItem.quantity > 1 &&
        ["1 PACK", "2 PACK"].some((opt) =>
            cartLineItem?.variant_options.some((v) => v.toLowerCase() == opt.toLowerCase()),
        );

    const ItemProperties = cartConfig?.cart_settings?.show_product_properties && !!productOptions?.length && (
        <div
            className={styles.cartItemOptions}
            style={{
                color: cartConfig?.cart_design?.cartline_card_text_color,
                fontSize: cartConfig?.cart_design?.cartline_card_properties_size,
                fontWeight: cartConfig?.cart_design?.cartline_card_properties_style?.split(" ")[0],
                fontStyle: cartConfig?.cart_design?.cartline_card_properties_style?.split(" ")[1],
            }}
        >
            <span className={styles.cartItemOption}>
                {shopCheck
                    ? "Multi-Pack"
                    : productOptions
                          .filter((option) => option?.name?.toLowerCase() !== "title")
                          .map((option: { name: string; value: string }) => option.value || "-")
                          .join("/")}
            </span>
        </div>
    );
    const ItemCustomProperties =
        !!cartConfig?.cart_custom_text?.length &&
        cartConfig?.cart_custom_text?.map((textKey) => {
            if (cartLineItem?.properties?.[textKey]) {
                return (
                    <div
                        className={styles.cartItemOptions}
                        style={{
                            color: cartConfig?.cart_design?.cartline_card_text_color,
                            fontSize: cartConfig?.cart_design?.cartline_card_properties_size,
                            fontWeight: cartConfig?.cart_design?.cartline_card_properties_style?.split(" ")[0],
                            fontStyle: cartConfig?.cart_design?.cartline_card_properties_style?.split(" ")[1],
                        }}
                    >
                        <span className={styles.cartItemOption}>
                            {textKey}: {cartLineItem?.properties?.[textKey]}
                        </span>
                    </div>
                );
            }
        });

    const updatedTitle = cartLineItem?.variant_title
        ? `${cartLineItem.product_title} - ${cartLineItem.variant_title}`
        : cartLineItem.product_title;

    const decodedTitle = new DOMParser()?.parseFromString(updatedTitle, "text/html")?.documentElement.textContent;

    // aria-disabled instead of disabled: a disabled button drops keyboard focus to <body> mid-update
    const isDecreaseDisabled = cartLineItem?.quantity <= (cartLineItem?.quantity_rule?.min ?? 1) || actionInProgress;
    const isIncreaseDisabled =
        actionInProgress ||
        (cartLineItem?.quantity_rule?.max != null && cartLineItem?.quantity >= cartLineItem?.quantity_rule?.max);

    const handleRedirectToProduct = () => {
        if (standAlone && cartLineItem?.url) {
            window.open(cartLineItem.url, "_blank");
        }
    };

    // variant Ids
    let variantIds: string[] = [];
    let variantQuantities: Record<string, number> = {};
    try {
        let rawItems: any[] = [];
        if (cartLineItem?.properties?._options) {
            rawItems = JSON.parse(cartLineItem.properties._options);
        } else if (cartLineItem?.properties?._builder_subproducts) {
            rawItems = JSON.parse(cartLineItem.properties._builder_subproducts);
        }

        if (Array.isArray(rawItems)) {
            rawItems.forEach((item: any) => {
                let id = "";
                let qty = 1;
                if (typeof item === "object" && item !== null) {
                    id = (item.variant_id || item.id)?.toString();
                    qty = Number(item.quantity) || 1;
                } else {
                    id = item?.toString();
                }

                if (id) {
                    const numericId = getProductId(id);
                    if (numericId) {
                        variantQuantities[numericId] = (variantQuantities[numericId] || 0) + qty;
                        if (!variantIds.includes(numericId)) {
                            variantIds.push(numericId);
                        }
                    }
                }
            });
        }
    } catch (err) {
        console.warn("Failed to parse variant ids:", err);
        variantIds = [];
        variantQuantities = {};
    }

    const enableProductVariantsInfo = Array.isArray(variantIds) && variantIds.length > 0;

    return (
        <div
            key={cartLineItem.id + index}
            id={cartLineItem.id?.toString()}
            className={styles.cartItem}
            style={{
                pointerEvents: actionInProgress ? "none" : "all",
                border: cartGlobal.removeBorders ? "none" : "1px solid #dedede",
                borderBottom: cartGlobal?.removeBorders ? "1px solid #dedede" : "",
                borderRadius: cartGlobal?.removeBorders ? 8 : "",
                paddingBottom: cartGlobal?.removeBorders ? 20 : "",
                background: cartConfig?.cart_design?.cartline_card_bg_color,
            }}
        >
            {standAlone && microActionProgress == index && (
                <div className={styles.cartItemLoading}>
                    <Spinner color={"#939393"} size={50} />
                </div>
            )}
            <div className={styles.cartItemImageWrapper}>
                {cartLineItem?.image ? (
                    <img
                        src={cartLineItem.image}
                        alt={decodedTitle}
                        onClick={handleRedirectToProduct}
                        style={{
                            width: "100%",
                            height: "100%",
                            padding: 2,
                            objectFit: "contain",
                            objectPosition: "center",
                            borderRadius: 8,
                        }}
                    />
                ) : (
                    <DefaultImageIcon w={"100%"} h={"100%"} />
                )}
            </div>
            <div className={styles.cartItemDetails}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 5,
                    }}
                >
                    <div
                        style={{
                            ...(productCardLayout.pricePosition == CartItemPositions.TOP && {
                                display: "flex",
                                justifyContent: "space-between",
                            }),
                        }}
                    >
                        <div
                            className={styles.cartItemTitle}
                            style={{
                                color: cartConfig?.cart_design?.cartline_card_text_color,
                                fontSize: cartConfig?.cart_design?.cartline_card_title_size,
                                fontWeight: cartConfig?.cart_design?.cartline_card_title_style?.split(" ")[0],
                                fontStyle: cartConfig?.cart_design?.cartline_card_title_style?.split(" ")[1],
                            }}
                        >
                            {productCardLayout.pricePosition == CartItemPositions.TOP ? (
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 5,
                                    }}
                                >
                                    {decodedTitle}
                                    {ItemProperties}
                                    {ItemCustomProperties}
                                    {RenderSellingPlan}
                                </div>
                            ) : (
                                <>{decodedTitle}</>
                            )}
                            {productCardLayout.deletePosition == CartItemPositions.TOP && RenderDelete}
                        </div>

                        {productCardLayout.pricePosition == CartItemPositions.TOP && RenderPrice}
                    </div>
                    {productCardLayout.pricePosition == CartItemPositions.BOTTOM && ItemOriginalPrice}
                    {productCardLayout.pricePosition == CartItemPositions.BOTTOM && ItemProperties}
                    {productCardLayout.pricePosition == CartItemPositions.BOTTOM && ItemCustomProperties}
                    {productCardLayout.pricePosition == CartItemPositions.BOTTOM && RenderSellingPlan}
                </div>

                {enableProductVariantsInfo && (
                    <CartItemVariantsInfo
                        standalone={standAlone}
                        storefrontAccessToken={sft}
                        variantIds={variantIds}
                        quantity={cartLineItem?.quantity}
                        variantQuantities={variantQuantities}
                    />
                )}
                <div
                    className={styles.cartItemBottomSection}
                    style={{
                        alignItems: productCardLayout.pricePosition == CartItemPositions.TOP ? "baseline" : "center",
                        marginTop: 15,
                    }}
                >
                    <div
                        className={styles.quantityContainer}
                        style={{
                            borderRadius: productCardLayout.qtyRadius,
                            visibility:
                                cartLineItem?.properties?.[ATTRIBUTE_KEY_SHORTCODES.__kbd]?.[
                                    ATTRIBUTE_KEY_SHORTCODES.__freeGiftRewards
                                ] != null
                                    ? "hidden"
                                    : cartLineItem?.properties?.[ATTRIBUTE_KEY_SHORTCODES.__kbd]?.[
                                            ATTRIBUTE_KEY_SHORTCODES.__triggerRewards
                                        ]
                                      ? "hidden"
                                      : "visible",
                        }}
                    >
                        <button
                            type="button"
                            aria-label={`Decrease quantity of ${decodedTitle}`}
                            aria-disabled={isDecreaseDisabled}
                            className={`${styles.quantityButton} ${isDecreaseDisabled && styles.disabled}`}
                            style={{
                                color: isDecreaseDisabled ? "" : cartConfig?.cart_design?.cartline_card_qty_color,
                            }}
                            onClick={(e) => {
                                e?.preventDefault();
                                e?.stopPropagation();
                                if (isDecreaseDisabled) return;
                                setMicroActionProgress(index);
                                handleQuantity(e, CartOperations.DECREASE, cartLineItem.key);
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 19,
                                    fontWeight: 350,
                                    position: "relative",
                                    top: -1,
                                    color: isDecreaseDisabled ? "" : cartConfig?.cart_design?.cartline_card_qty_color,
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ height: 10, width: 10 }}
                                    viewBox="0 0 10 2"
                                >
                                    <path
                                        fill="currentColor"
                                        fill-rule="evenodd"
                                        d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 1 1 0 1H1A.5.5 0 0 1 .5 1"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                            </span>
                        </button>
                        {productCardLayout.qtySeparator && (
                            <span
                                className={styles.quantityContainerSeparator}
                                style={{
                                    background: "#b5b5b5",
                                }}
                            ></span>
                        )}
                        <div
                            className={styles.quantity}
                            aria-live="polite"
                            style={{
                                color: cartConfig?.cart_design?.cartline_card_qty_color,
                            }}
                        >
                            {cartLineItem?.quantity}
                        </div>
                        {productCardLayout.qtySeparator && (
                            <span
                                className={styles.quantityContainerSeparator}
                                style={{
                                    background: "#b5b5b5",
                                }}
                            ></span>
                        )}
                        <button
                            type="button"
                            aria-label={`Increase quantity of ${decodedTitle}`}
                            aria-disabled={isIncreaseDisabled}
                            className={`${styles.quantityButton} ${isIncreaseDisabled && styles.disabled} `}
                            onClick={(e) => {
                                e?.preventDefault();
                                e?.stopPropagation();
                                if (isIncreaseDisabled) return;
                                setMicroActionProgress(index);
                                handleQuantity(e, CartOperations.INCREASE, cartLineItem.key);
                            }}
                            style={{
                                color: isIncreaseDisabled ? "" : cartConfig?.cart_design?.cartline_card_qty_color,
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 14,
                                    fontWeight: 450,
                                    position: "relative",
                                    color: "inherit",
                                    top: 0,

                                    lineHeight: "normal",
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    style={{ height: 10, width: 10 }}
                                    viewBox="0 0 10 10"
                                >
                                    <path
                                        fill="currentColor"
                                        fill-rule="evenodd"
                                        d="M1 4.51a.5.5 0 0 0 0 1h3.5l.01 3.5a.5.5 0 0 0 1-.01V5.5l3.5-.01a.5.5 0 0 0-.01-1H5.5L5.49.99a.5.5 0 0 0-1 .01v3.5l-3.5.01z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                            </span>
                        </button>
                    </div>
                    {productCardLayout.deletePosition == CartItemPositions.BOTTOM && RenderDelete}
                    {productCardLayout.pricePosition == CartItemPositions.BOTTOM && RenderPrice}
                </div>
                {!!cartLineItem?.line_level_discount_allocations?.length && (
                    <span className={styles.cartItemDiscount} style={{ color: "green" }}>
                        <RenderDiscountsBadges
                            arr={cartLineItem?.line_level_discount_allocations}
                            bgColor={cartConfig?.cart_design?.cartline_card_discount_bg_color}
                            textColor={cartConfig?.cart_design?.cartline_card_discount_text_color}
                        />
                    </span>
                )}
            </div>
        </div>
    );
};

const CartProducts = ({
    cart,
    cartConfig,
    handleUpdateItem,
    currency,
    money_format,
    actionInProgress,
    standAlone,
    clearCart,
    featIndex = -1,
    sft,
}: {
    cartConfig: CartConfigInterface;
    cart: any;
    handleUpdateItem: (payload?: any) => void;
    actionInProgress: boolean;
    currency: string;
    money_format: string;
    standAlone: boolean;
    clearCart: (() => Promise<any>) | (() => void);
    featIndex: number;
    sft: string;
}) => {
    const filterCartLine = (cl: CartLineItem) => {
        if (
            cartConfig?.additional_product?.shipping_protection_id?.includes(cl?.product_id as unknown as string) ||
            cartConfig?.additional_product?.gift_wrapping_id?.includes(cl?.product_id as unknown as string)
        ) {
            return false;
        } else {
            return true;
        }
    };

    const cartLines = cart?.items.filter(filterCartLine);

    if (isSpiritEmbassyShop()) {
        cartLines.forEach((line: any) => {
            if (line.final_price === 0) {
                line.line_level_discount_allocations = [
                    {
                        amount: line.original_line_price,
                        discount_application: {
                            title: "Free Gift",
                        },
                    },
                ];
            }
        });

        cartLines.sort((a: any, b: any) => Number(a.final_price === 0) - Number(b.final_price === 0));
    }

    useEffect(() => {
        if (cart?.items.length > 0) {
            const total = cart?.items.filter(filterCartLine)?.length;
            if (total == 0) {
                clearCart();
            }
        }
    }, [cart?.items]);

    return (
        <span className={styles.cartItems} style={{ paddingTop: featIndex == 0 ? 15 : 0 }}>
            {cartLines.map((cartLine: CartLineItem, idx: number) => (
                <CartLine
                    cartLineItem={cartLine}
                    index={idx}
                    cart={cart}
                    key={idx + 1}
                    handleUpdateItem={handleUpdateItem}
                    currency={currency}
                    money_format={money_format}
                    actionInProgress={actionInProgress}
                    cartConfig={cartConfig}
                    standAlone={standAlone}
                    sft={sft}
                    clearCart={clearCart}
                    allCartLinesCount={cartLines.length}
                />
            ))}
        </span>
    );
};

export default CartProducts;
